/**
 * JSM Land use-case onboarding experiment (jsmgrowth_519_jsm_land_use_case_onboarding)
 *
 * Variant-only card grid: shows 6 team types upfront, with the remaining
 * 8 collapsed behind a "+ Show 8 more options" button.
 *
 * This component is ONLY rendered for the `variant` cohort of the experiment.
 * The original CardsGrid is used for all other cohorts — it is NOT modified.
 */
import { useCallback, useMemo, useState } from 'react';

import { css, keyframes } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import AddIcon from '@atlaskit/icon/core/add';
import { Box, Grid, media, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { useConfirmationPage } from '../../hooks/use-confirmation-page';
import {
	USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
	USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE,
	USER_SEGMENTATION_TEAM_TYPE_DESIGN,
	USER_SEGMENTATION_TEAM_TYPE_FINANCE,
	USER_SEGMENTATION_TEAM_TYPE_HR,
	USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
	USER_SEGMENTATION_TEAM_TYPE_LEGAL,
	USER_SEGMENTATION_TEAM_TYPE_MARKETING,
	USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
	USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
	USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_SALES,
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
	UserSegmentationTeamTypes,
} from '../../models/user-segmentation.ts';

import { UserSegCard } from './card.tsx';
import { CardsMapObject, useCardsMap } from './cards-grid.tsx';

const messages = defineMessages({
	dataScienceTeamType: {
		id: 'UserSeg.teamTypeSelection.jsmUseCase.dataScience',
		defaultMessage: 'Data Science',
	},
	showMoreOptions: {
		id: 'UserSeg.teamTypeSelection.jsmUseCase.showMore',
		defaultMessage: 'Show {count} more options',
	},
});

const JSM_USE_CASE_COLLAPSED_LEFT_TEAM_TYPES: readonly UserSegmentationTeamTypes[] = [
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
	USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
	USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
] as const;

const JSM_USE_CASE_COLLAPSED_RIGHT_TEAM_TYPES: readonly UserSegmentationTeamTypes[] = [
	USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
	USER_SEGMENTATION_TEAM_TYPE_HR,
	USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
] as const;

const JSM_USE_CASE_EXPANDED_LEFT_TEAM_TYPES: readonly UserSegmentationTeamTypes[] = [
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
	USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
	USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
	USER_SEGMENTATION_TEAM_TYPE_MARKETING,
	USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_FINANCE,
	USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE,
] as const;

const JSM_USE_CASE_EXPANDED_RIGHT_TEAM_TYPES: readonly UserSegmentationTeamTypes[] = [
	USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
	USER_SEGMENTATION_TEAM_TYPE_HR,
	USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_DESIGN,
	USER_SEGMENTATION_TEAM_TYPE_LEGAL,
	USER_SEGMENTATION_TEAM_TYPE_SALES,
	USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
] as const;

const JSM_USE_CASE_COLLAPSED_TEAM_TYPES = [
	...JSM_USE_CASE_COLLAPSED_LEFT_TEAM_TYPES,
	...JSM_USE_CASE_COLLAPSED_RIGHT_TEAM_TYPES,
] as const;

const JSM_USE_CASE_ALL_TEAM_TYPES = [
	...JSM_USE_CASE_EXPANDED_LEFT_TEAM_TYPES,
	...JSM_USE_CASE_EXPANDED_RIGHT_TEAM_TYPES,
] as const;

const getCardsForTeamTypes = (
	cardsMap: CardsMapObject[],
	teamTypes: readonly UserSegmentationTeamTypes[],
) =>
	teamTypes
		.map((teamType) => cardsMap.find((card) => card.teamType === teamType))
		.filter((card): card is CardsMapObject => card !== undefined);

type JsmUseCaseCardColumnProps = {
	readonly cards: CardsMapObject[];
	readonly startingIndex: number;
	readonly cardLabelOverrides: Partial<Record<UserSegmentationTeamTypes, string>>;
	readonly onCardClick: () => void;
	readonly afterCardClick: () => void;
	readonly cardSelected: boolean;
};

const JsmUseCaseCardColumn = ({
	cards,
	startingIndex,
	cardLabelOverrides,
	onCardClick,
	afterCardClick,
	cardSelected,
}: JsmUseCaseCardColumnProps) => (
	<Stack space="space.200" xcss={cardColumnStyles}>
		{cards.map((cardObject, index) => (
			<UserSegCard
				key={cardObject.teamType}
				idx={startingIndex + index}
				{...cardObject}
				card={cardLabelOverrides[cardObject.teamType] ?? cardObject.card}
				onClick={onCardClick}
				afterClick={afterCardClick}
				disabled={cardSelected}
			/>
		))}
	</Stack>
);

export const JsmUseCaseCardsGrid = () => {
	const intl = useIntl();
	const cardsMap = useCardsMap();
	const {
		userSegmentationContext: { isTeamTypesExpanded, setIsTeamTypesExpanded },
	} = useConfirmationPage();
	const cardLabelOverrides = useMemo(
		() => ({
			[USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE]: intl.formatMessage(messages.dataScienceTeamType),
		}),
		[intl],
	);

	const [cardSelected, setCardSelected] = useState(false);

	const onCardClick = useCallback(() => {
		setCardSelected(true);
	}, []);
	const afterCardClick = useCallback(() => {
		setCardSelected(false);
	}, []);

	const leftCards = getCardsForTeamTypes(
		cardsMap,
		isTeamTypesExpanded
			? JSM_USE_CASE_EXPANDED_LEFT_TEAM_TYPES
			: JSM_USE_CASE_COLLAPSED_LEFT_TEAM_TYPES,
	);
	const rightCards = getCardsForTeamTypes(
		cardsMap,
		isTeamTypesExpanded
			? JSM_USE_CASE_EXPANDED_RIGHT_TEAM_TYPES
			: JSM_USE_CASE_COLLAPSED_RIGHT_TEAM_TYPES,
	);
	const hiddenCardCount =
		JSM_USE_CASE_ALL_TEAM_TYPES.length - JSM_USE_CASE_COLLAPSED_TEAM_TYPES.length;

	const showMoreText = intl.formatMessage(messages.showMoreOptions, {
		count: hiddenCardCount,
	});

	return (
		<Box xcss={contentWrapperStyles}>
			<div css={isTeamTypesExpanded && expandedGridStyles}>
				<Grid xcss={gridContainerBase} rowGap="space.200" columnGap="space.300">
					<JsmUseCaseCardColumn
						cards={leftCards}
						startingIndex={0}
						cardLabelOverrides={cardLabelOverrides}
						onCardClick={onCardClick}
						afterCardClick={afterCardClick}
						cardSelected={cardSelected}
					/>
					<JsmUseCaseCardColumn
						cards={rightCards}
						startingIndex={leftCards.length}
						cardLabelOverrides={cardLabelOverrides}
						onCardClick={onCardClick}
						afterCardClick={afterCardClick}
						cardSelected={cardSelected}
					/>
				</Grid>
			</div>
			{!isTeamTypesExpanded && (
				<button
					css={showMoreButtonStyles}
					onClick={() => setIsTeamTypesExpanded(true)}
					aria-expanded={isTeamTypesExpanded}
					aria-label={`+ ${showMoreText}`}
					type="button"
				>
					<AddIcon label="" color="currentColor" />
					<span>{showMoreText}</span>
				</button>
			)}
		</Box>
	);
};

const revealCardKeyframes = keyframes({
	from: {
		opacity: 0,
		transform: 'translateY(-8px)',
	},
	to: {
		opacity: 1,
		transform: 'translateY(0)',
	},
});

const expandedGridStyles = css({
	animation: `${revealCardKeyframes} 200ms ease-out both`,
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});

const contentWrapperStyles = xcss({
	width: '100%',
	marginInlineStart: 'auto',
	marginInlineEnd: 'auto',
	maxWidth: '744px',
});

const gridContainerBase = xcss({
	width: '100%',
	boxSizing: 'border-box',
	paddingBlockStart: 'space.025',
	paddingBlockEnd: 'space.025',
	paddingInlineStart: 'space.025',
	paddingInlineEnd: 'space.025',
	gridTemplateColumns: '1fr',
	[media.above.sm]: {
		gridTemplateColumns: '1fr 1fr',
	},
});

const cardColumnStyles = xcss({
	[media.above.sm]: {
		minWidth: '0',
	},
});

const showMoreButtonStyles = css({
	width: '100%',
	marginBlockStart: token('space.300'),
	paddingBlockStart: token('space.150'),
	paddingBlockEnd: token('space.150'),
	paddingInlineStart: token('space.200'),
	paddingInlineEnd: token('space.200'),
	backgroundColor: token('color.background.neutral.subtle'),
	border: `1px dashed ${token('color.border')}`,
	borderRadius: token('radius.large', '8px'),
	cursor: 'pointer',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	columnGap: token('space.100'),
	color: token('color.text.subtle'),
	font: token('font.body'),
	fontWeight: token('font.weight.semibold'),
	textAlign: 'center',
	transition: 'background-color 150ms ease-out',
	':hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});
